import { observer } from "mobx-react-lite";
import { useContext, useMemo, useRef, useState } from "react";

import { DateTime } from "@bps/date-time";
import {
  dataAttribute,
  DataAttributes,
  FontSizes,
  IColumn,
  IDetailsRowStyles,
  mergeStyleSets,
  NoDataTile,
  SelectionMode,
  Stack,
  Text,
  useFormContext,
  useTheme
} from "@bps/fluent-ui";
import { renderCalendarEventWrapper } from "@modules/booking/screens/booking-calendar/components/utils.tsx";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Selection } from "@ui-components/ShimmeredDetailsList/Selection.ts";
import { ShimmeredDetailsList } from "@ui-components/ShimmeredDetailsList/ShimmeredDetailsList.tsx";

import { listWrapperStyles } from "../AppointmentInformationModal.styles.ts";
import { AppointmentInformationModalContext } from "../AppointmentInformationModalContext.ts";
import { PrefAndInformationPivotTabTypes } from "../AppointmentInformationPivotTabs.types.ts";
import {
  AppointmentCancellationReason,
  AppointmentInformationApptType,
  AppointmentInformationClaim,
  AppointmentInformationDate,
  AppointmentInformationDuration,
  AppointmentInformationLocation,
  AppointmentInformationProvider,
  AppointmentInformationStatus
} from "../AppointmentInformationTableCells.tsx";
import { AppointmentInformationModalButtonsOld } from "./AppointmentInformationModalButtonsOld.tsx";
import { ApptinfoFilterTypeOld } from "./AppointmentInformationModalInnerOld.tsx";
import { filterData } from "./utils.ts";

interface AppointmentInformationTableProps {
  items: CalendarEvent[];
  claimId: string | undefined;
  onDismiss: () => void;
  isLoading: boolean;
  selectedTab: PrefAndInformationPivotTabTypes;
}

export const AppointmentInformationTableOld: React.FunctionComponent<AppointmentInformationTableProps> =
  observer(({ items, claimId, onDismiss, isLoading, selectedTab }) => {
    const { core } = useStores();
    const theme = useTheme();
    const helper = useContext(AppointmentInformationModalContext);

    const [selectionCount, setSelectionCount] = useState<number>();

    const selection = useRef(
      new Selection({
        onSelectionChanged: () => {
          const selectedItems = selection.current.getSelection();
          const selectedCount = selectedItems.length;

          if (selectedCount) {
            const selectedItem = selectedItems[0];
            if (selectedItem) {
              // Selection allows using items that don't have a "key" property, however then ShimmeredDetails
              //  list complains.  I don't want to do an app-wide refactor right now so I'm overriding the
              //  typing using as unknown
              helper.setSelectedCalendarEventId(
                (selectedItem as unknown as CalendarEvent).id
              );
            }
          }
          if (selectionCount !== selectedCount) {
            setSelectionCount(selectedCount);
          }
        }
      })
    );

    const columns: IColumn[] = [
      {
        key: "date",
        isRowHeader: true,
        minWidth: 90,
        maxWidth: 100,
        name: "Date",
        onRender: (calendarEvent: CalendarEvent) => (
          <AppointmentInformationDate
            calendarEvent={calendarEvent}
            onDismiss={onDismiss}
          />
        ),
        isMultiline: true
      },
      {
        key: "status",
        minWidth: 80,
        maxWidth: 90,
        name: "Status",
        isMultiline: true,
        onRender: (calendarEvent: CalendarEvent) => (
          <AppointmentInformationStatus calendarEvent={calendarEvent} />
        )
      },
      {
        key: "type",
        minWidth: 130,
        maxWidth: 230,
        name: "Appointment type",
        isMultiline: true,
        onRender: (calendarEvent: CalendarEvent) => (
          <AppointmentInformationApptType calendarEvent={calendarEvent} />
        )
      },
      {
        key: "duration",
        minWidth: 70,
        maxWidth: 90,
        name: "Duration",
        isMultiline: true,
        onRender: (calendarEvent: CalendarEvent) => (
          <AppointmentInformationDuration calendarEvent={calendarEvent} />
        ),
        styles: mergeStyleSets({
          cellTitle: {
            justifyContent: "flex-end"
          }
        })
      },
      {
        key: "provider",
        minWidth: 100,
        maxWidth: 150,
        name: "Provider",
        isMultiline: true,
        onRender: (calendarEvent: CalendarEvent) => (
          <AppointmentInformationProvider calendarEvent={calendarEvent} />
        )
      }
    ];

    if (core.hasMultipleActiveLocations) {
      columns.push({
        key: "location",
        minWidth: 100,
        maxWidth: 150,
        name: "Location",
        isMultiline: true,
        onRender: (calendarEvent: CalendarEvent) => (
          <AppointmentInformationLocation calendarEvent={calendarEvent} />
        )
      });
    }

    if (core.isNZTenant && !claimId) {
      columns.push({
        key: "claim",
        minWidth: 80,
        maxWidth: 80,
        name: "Claim no.",
        onRender: (calendarEvent: CalendarEvent) => (
          <AppointmentInformationClaim calendarEvent={calendarEvent} />
        )
      });
    }

    columns.push({
      key: "cancellationReason",
      minWidth: 300,
      maxWidth: 300,
      name: "Reason",
      isMultiline: true,
      onRender: (calendarEvent: CalendarEvent) => (
        <AppointmentCancellationReason calendarEvent={calendarEvent} />
      )
    });

    const {
      actions: { reset },
      state: { values }
    } = useFormContext<ApptinfoFilterTypeOld>();

    const filteredItems = useMemo(
      () => items.filter((item: CalendarEvent) => filterData(item, values)),
      [items, values]
    );

    const today = DateTime.now().startOf("day");

    const hasBothPastAndFutureAppointments =
      filteredItems.some(
        (calendarEvent: CalendarEvent) =>
          calendarEvent.startDateTime.startOf("day") >= today
      ) &&
      filteredItems.some(
        (calendarEvent: CalendarEvent) =>
          calendarEvent.startDateTime.startOf("day") < today
      );

    const latestPastRecord = hasBothPastAndFutureAppointments
      ? filteredItems.find(
          (x: CalendarEvent) =>
            x.startDateTime.startOf("day") < DateTime.now().startOf("day")
        )
      : undefined;

    return (
      <>
        <Stack
          styles={listWrapperStyles}
          {...dataAttribute(DataAttributes.Element, "list-wrapper")}
          grow
        >
          <ShimmeredDetailsList
            setKey="appt-table-key"
            enableShimmer={isLoading}
            onRenderRow={props => {
              const record: CalendarEvent | undefined = props?.item;

              const showLineSeparator =
                hasBothPastAndFutureAppointments && record === latestPastRecord;

              const detailRowStyles: Partial<IDetailsRowStyles> | undefined = {
                checkCell: {
                  display: "inline-flex",
                  alignItems: "center"
                },
                ...(showLineSeparator
                  ? {
                      root: {
                        borderTop: `1px solid ${theme.palette.redDark}`
                      }
                    }
                  : {})
              };

              return renderCalendarEventWrapper(
                props,
                undefined,
                detailRowStyles
              );
            }}
            items={filteredItems}
            selection={selection.current}
            selectionMode={SelectionMode.single}
            selectionPreservedOnEmptyClick
            columns={columns}
          />
          {items.length === 0 && !isLoading && (
            <Stack
              grow
              verticalFill
              tokens={{ childrenGap: 20 }}
              horizontalAlign="center"
              verticalAlign="center"
              styles={{
                root: {
                  background: theme.palette.neutralLighterAlt,
                  padding: 8,
                  height: 300,
                  fontSize: FontSizes.size14,
                  fontStyle: "italic"
                }
              }}
            >
              <Text
                styles={{
                  root: {
                    color: theme.palette.neutralSecondary
                  }
                }}
              >
                {selectedTab === PrefAndInformationPivotTabTypes.Upcoming
                  ? "No upcoming appointments"
                  : "No past appointments"}
              </Text>
            </Stack>
          )}
          {items.length !== 0 && filteredItems.length === 0 && !isLoading && (
            <Stack
              grow
              verticalFill
              horizontalAlign="center"
              verticalAlign="center"
              styles={{
                root: {
                  padding: 8,
                  height: "300px",
                  fontSize: FontSizes.size14,
                  fontStyle: "italic"
                }
              }}
            >
              <NoDataTile
                textProps={{ text: "0 matches found" }}
                linkProps={{
                  text: "Clear filters",
                  onClick: () => reset({})
                }}
                showBoxShadow={false}
              />
            </Stack>
          )}
        </Stack>
        <AppointmentInformationModalButtonsOld
          onDismiss={onDismiss}
          selectionCount={selectionCount}
          apptItems={filteredItems}
          noAppointments={items.length === 0}
        />
      </>
    );
  });
